import { useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { twMerge } from "tailwind-merge";

import { EntityContext, ManagementType } from "../../../types/commonTypes";

import "./styles.css";

interface Action {
  text: string | React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
}

interface ActionsMenuProps {
  entityContext?: EntityContext;
  actions: Action[];
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLDivElement>;
}

const TOP_EXTRA_SPACE = 7;
const LEFT_EXTRA_SPACE = 5;

export const ActionsMenu = ({
  entityContext,
  actions,
  isOpen,
  onClose,
  triggerRef,
}: ActionsMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
    direction: "top" | "bottom";
  }>({ top: 0, left: 0, width: 0, direction: "bottom" });

  const typeText =
    entityContext?.type === ManagementType.GROUP
      ? "Groupe"
      : entityContext?.type === ManagementType.AGENCY
        ? "Agence"
        : entityContext?.type === ManagementType.TEAM
          ? "Equipe"
          : entityContext?.type === ManagementType.USER
            ? "Utilisateur"
            : "";

  useEffect(() => {
    if (!isOpen || !menuRef.current || !triggerRef.current) return;

    const updatePosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      const menuRect = menuRef.current?.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      if (!triggerRect || !menuRect) return;

      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;
      let direction: "top" | "bottom" = "bottom";
      let top = triggerRect.bottom + TOP_EXTRA_SPACE;
      if (spaceBelow < menuRect.height && spaceAbove > spaceBelow) {
        direction = "top";
        top = triggerRect.top - menuRect.height - TOP_EXTRA_SPACE;
        if (top < 0) {
          // If not enough space above, fallback to bottom
          direction = "bottom";
          top = triggerRect.bottom + TOP_EXTRA_SPACE;
        }
      } else {
        if (top + menuRect.height > viewportHeight) {
          // If not enough space below, fallback to top
          direction = "top";
          top = triggerRect.top - menuRect.height - TOP_EXTRA_SPACE;
        }
      }
      // Ensure menu doesn't overflow horizontally
      let left = triggerRect.right - menuRect.width + LEFT_EXTRA_SPACE;
      if (left < 0) left = 0;
      if (left + menuRect.width > viewportWidth)
        left = viewportWidth - menuRect.width - LEFT_EXTRA_SPACE;
      setPosition({
        top: Math.max(top, 0),
        left,
        width: triggerRect.width,
        direction,
      });
    };

    // Add scroll event listener to close menu
    const handleScroll = () => {
      onClose();
    };

    updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, triggerRef, onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  const handleClick = (action: Action) => {
    action?.onClick?.();
    onClose();
  };

  if (!isOpen) return null;

  const menu = (
    <div
      ref={menuRef}
      style={{
        top: position.top,
        left: position.left,
      }}
      className="fixed z-[9999] w-max max-w-[250px] px-2.5 py-2 bg-white rounded-lg overflow-hidden shadow-lg border border-gray-200 right-0"
    >
      {entityContext && (
        <div
          className={twMerge(
            "text-sm text-center px-1.5 py-2 bg-slate-100/80",
            actions?.length && "mb-2"
          )}
        >
          <span className="text-[13px] text-gray-600 mr-[5px]">{typeText}</span>
          <span className="text-[13px] font-bold">{entityContext.name}</span>
        </div>
      )}
      {actions.map((action, index) => (
        <div
          key={index}
          className="actions-menu-item"
          onClick={() => handleClick(action)}
        >
          {action.icon && <div className="mt-[1px] w-4">{action.icon}</div>}
          <div className="text-[13px]">{action.text}</div>
        </div>
      ))}
    </div>
  );

  return createPortal(menu, document.body);
};
